import React from 'react';
import { Popup } from '@gravity-ui/uikit';
import { NodeSelection } from 'prosemirror-state';
import { findDomRefAtPos, findParentNodeOfType, findSelectedNodeOfType } from 'prosemirror-utils';
import { cn } from '../../classname';
import { getReactRendererFromState } from '../../extensions';
import { ErrorLoggerBoundary } from '../../react-utils/ErrorBoundary';
import './index.css';
export const BaseTooltipCn = cn('base-tooltip');
export const defaultPlacement = [
    'bottom-start',
    'top-start',
    'bottom-end',
    'top-end',
];
const b = BaseTooltipCn;
export const baseTooltipContentCn = cn('base-tooltip-content');
export class BaseTooltipPluginView {
    constructor(view, options) {
        this.currentNode = null;
        this.manualHidden = false;
        this.destroyed = false;
        this.focusTrackingRafId = null;
        this.onEscapeDown = () => {
            if (this.disableHideOnEscapeKeyDown)
                return false;
            if (this.currentNode && !this.manualHidden) {
                this.hidePopupManual();
                return true;
            }
            return false;
        };
        this.changeAttrsCb = (attrs, options) => {
            if (this.currentNode) {
                let tr = this.view.state.tr.setNodeMarkup(this.currentNode.pos, undefined, Object.assign(Object.assign({}, this.currentNode.node.attrs), attrs), (options === null || options === void 0 ? void 0 : options.marks) || []);
                if (options === null || options === void 0 ? void 0 : options.setSelection) {
                    tr = tr.setSelection(NodeSelection.create(tr.doc, this.currentNode.pos));
                }
                this.view.dispatch(tr);
                if (options === null || options === void 0 ? void 0 : options.setFocus) {
                    this.view.focus();
                }
                if (options === null || options === void 0 ? void 0 : options.hideTooltip) {
                    this.hidePopupManual();
                }
            }
        };
        this.onOutsideClick = () => {
            this.hidePopupManual();
        };
        this.popupCloseHandler = (_e, reason) => {
            if (this.disableHideOnEscapeKeyDown && reason === 'escapeKeyDown')
                return;
            this.hidePopupManual();
            if (reason === 'outsideClick' && !this.view.hasFocus()) {
                this.startTrackingViewFocus();
            }
        };
        this.view = view;
        this.idPrefix = options.idPrefix;
        this.content = options.content;
        this.nodeType = options.nodeType;
        this.popupPlacement = options.popupPlacement;
        this.disableHideOnEscapeKeyDown = options.disableHideOnEscapeKeyDown;
    }
    update(view) {
        this.view = view;
        this.updateTooltipView();
    }
    destroy() {
        var _a;
        this.destroyed = true;
        (_a = this.renderItem) === null || _a === void 0 ? void 0 : _a.remove();
        this.renderItem = undefined;
    }
    setCurrentNode(node) {
        const view = this.view;
        const domAtPos = view.domAtPos.bind(view);
        const { selection } = view.state;
        const { $from, $to } = selection;
        const parent = findSelectedNodeOfType(node)(selection) || findParentNodeOfType(node)(selection);
        if (!parent || !$from.node(parent.depth).eq($to.node(parent.depth))) {
            this.currentNode = null;
            this.manualHidden = false;
            this.stopTrackingViewFocus();
            return;
        }
        if (!this.currentNode ||
            !this.currentNode.node.eq(parent.node) ||
            this.currentNode.pos !== parent.pos) {
            this.manualHidden = false;
            this.stopTrackingViewFocus();
        }
        const { pos, node: parentNode } = parent;
        const dom = findDomRefAtPos(pos, domAtPos);
        this.currentNode = {
            pos,
            node: parentNode,
            dom,
        };
    }
    updateTooltipView() {
        var _a;
        const oldNode = this.currentNode;
        this.setCurrentNode(this.view.state.schema.nodes[this.nodeType.name]);
        // Do not rerender if we have the same node in selection
        if (oldNode &&
            ((_a = this.currentNode) === null || _a === void 0 ? void 0 : _a.node.eq(oldNode.node)) &&
            this.currentNode.pos === oldNode.pos &&
            this.currentNode.dom === oldNode.dom)
            return;
        this.render();
    }
    render() {
        var _a;
        if (this.destroyed)
            return;
        this.renderItem = (_a = this.renderItem) !== null && _a !== void 0 ? _a : this.createRenderItem();
        this.renderItem.rerender();
    }
    startTrackingViewFocus() {
        this.focusTrackingRafId = requestAnimationFrame(() => {
            if (this.view.hasFocus()) {
                this.focusTrackingRafId = null;
                this.manualHidden = false;
                this.render();
                return;
            }
            this.startTrackingViewFocus();
        });
    }
    stopTrackingViewFocus() {
        if (this.focusTrackingRafId !== null) {
            cancelAnimationFrame(this.focusTrackingRafId);
            this.focusTrackingRafId = null;
        }
    }
    hidePopupManual() {
        this.manualHidden = true;
        this.render();
    }
    renderContent(currentNode) {
        if (!this.content)
            return null;
        // hack for popup rerender
        window.dispatchEvent(new CustomEvent('scroll'));
        return (React.createElement(Popup, { open: true, hasArrow: false, contentClassName: b(), anchorRef: { current: currentNode.dom }, placement: this.popupPlacement || defaultPlacement, onClose: this.popupCloseHandler },
            React.createElement("div", null, this.content(this.view, currentNode, this.changeAttrsCb))));
    }
    createRenderItem() {
        return getReactRendererFromState(this.view.state).createItem(this.idPrefix, () => this.currentNode && !this.manualHidden ? (React.createElement(ErrorLoggerBoundary, null, this.renderContent(this.currentNode))) : null);
    }
}
